import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateScoutProfileImage, deleteScoutProfileImage, getScoutProfile } from '../../../features/scout/scoutProfileSlice';
import profileImage from '../../../assets/images/coachProfile.png';

export const ScoutSidebar = ({
  activeView = 'dashboard'
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { profile, profileImageLoading, loading } = useSelector((state) => state.scoutProfile);

  // Fetch profile if not available (e.g. deep reload on non-dashboard pages)
  useEffect(() => {
    if (!profile && !loading) {
      dispatch(getScoutProfile());
    }
  }, [profile, loading, dispatch]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      dispatch(updateScoutProfileImage(file));
    }
  };

  const handleImageDelete = () => {
    if (window.confirm('Are you sure you want to delete your profile image?')) {
      dispatch(deleteScoutProfileImage());
    }
  };

  const scoutName = profile
    ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
    : 'Loading...';

  const jobTitle = profile?.jobTitle || 'SCOUT';
  const organization = profile?.organization || profile?.school || 'ORGANIZATION';
  const profileImageUrl = profile?.profileImage || profileImage;

  const getButtonClass = (view) => {
    return `block w-full py-3 px-4 rounded-lg font-medium text-left transition-all ${
      activeView === view
        ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 border border-cyan-500/50 text-cyan-400'
        : 'bg-slate-700/30 hover:bg-slate-700/50 text-gray-300 hover:text-white'
    }`;
  };
console.log(profile)
  return (
    <div className="p-6">
      <div className="text-center mb-6">
        {/* Profile Image - Squared with rounded corners */}
        <div className="relative inline-block mb-4 group">
          <div className="w-40 h-40 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 mx-auto overflow-hidden border-2 border-slate-600">
            <img
              src={profileImageUrl}
              alt="Profile"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = profileImage; }}
            />
          </div>

          {/* Loading Overlay */}
          {profileImageLoading && (
            <div className="absolute inset-0 bg-black/70 rounded-xl flex items-center justify-center">
              <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Hover Overlay with Upload/Delete Buttons */}
          {!profileImageLoading && (
            <div className="absolute inset-0 bg-black/60 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="flex gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={profileImageLoading}
                  className="p-2 bg-cyan-500 hover:bg-cyan-400 rounded-full transition-colors disabled:opacity-50"
                  title="Upload new image"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                  </svg>
                </button>

                {profile?.profileImage && (
                  <button
                    onClick={handleImageDelete}
                    disabled={profileImageLoading}
                    className="p-2 bg-red-500 hover:bg-red-600 rounded-full transition-colors disabled:opacity-50"
                    title="Delete image"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Badge Icons */}
          {/* <div className="absolute bottom-2 left-2 w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-xl font-bold">S</span>
          </div>
          <div className="absolute bottom-2 right-2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
            <span className="text-2xl">⚾</span>
          </div> */}
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />

        {/* Name and Info */}
        <h3 className="text-white font-bold text-xl mb-1">{scoutName}</h3>
        <p className="text-gray-400 text-sm font-semibold mb-1">{jobTitle.toUpperCase()}</p>
        <p className="text-gray-500 text-xs mb-4">ORGANIZATION: {organization.toUpperCase()}</p>

        {/* Social Icons */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center hover:scale-110 transition-transform shadow-lg"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a
            href="https://twitter.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full bg-cyan-500 flex items-center justify-center hover:scale-110 transition-transform shadow-lg"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
            </svg>
          </a>
        </div>
      </div>

      {/* Navigation Links */}
      <div className="space-y-2">
        <button
          onClick={() => handleNavigation('/scout/dashboard')}
          className={getButtonClass('dashboard')}
        >
          Profile
        </button>

        <button
          onClick={() => handleNavigation('/scout/teams')}
          className={getButtonClass('teams')}
        >
          Teams
        </button>

        <button
          onClick={() => handleNavigation('/scout/uncommitted-tracker')}
          className={getButtonClass('uncommittedTracker')}
        >
          Uncommitted Tracker
        </button>

        {/* <button
          onClick={() => handleNavigation('/scout/team-analysis')}
          className={getButtonClass('teamAnalysis')}
        >
          Team Analysis
        </button> */}

        <button
          onClick={() => handleNavigation('/scout/statistics')}
          className={getButtonClass('statistics')}
        >
          Statistics
        </button>
      </div>
    </div>
  );
};

export default ScoutSidebar;
